/**
 * Refresh Stage 3: Competitor Content Extraction
 *
 * Fan-out stage: fetches and extracts content from each competitor URL
 * identified by refresh_2_serp. Shows competitor cards with above/below
 * tagging, per-item and batch run controls, and extraction detail view.
 *
 * Uses StagePanel's isLlmStage + extractionId to show per-competitor
 * prompt/output in the standard Details panel.
 */

import React, { useCallback, useRef, useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { jobsService } from '../../../services/jobsService';
import { blogRunsService } from '../../../services/blogRunsService';
import type { ArticleDocument } from '../types';

type BatchStatus = Record<string, { status: string; error?: string }>;

function CompetitorCards({ onSelectUrl }: { onSelectUrl: (url: string) => void }) {
  const { document, run, refreshDocument, refreshRun, refreshArticles } = useArticleContext();
  const urls = document?.targeting?.competitors?.urls || [];
  const extractions = (document?.targeting?.competitors?.extractions || []) as any[];
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [runningAll, setRunningAll] = useState(false);
  const [batchStatus, setBatchStatus] = useState<BatchStatus>({});
  const [runningSingle, setRunningSingle] = useState(false);
  const cancelRef = useRef(false);

  const extractedUrls = new Set(extractions.map((e: any) => e.url));

  const handleSelect = useCallback((url: string) => {
    setSelectedUrl(url);
    onSelectUrl(url);
  }, [onSelectUrl]);

  const runSingle = useCallback(async (entryUrl: string) => {
    if (!run?.run_id) return;
    setRunningSingle(true);
    try {
      await blogRunsService.resetFromStage(run.run_id, 'refresh_3_competitor_extract');
      await refreshRun();
      const { job_id } = await jobsService.submit('blog_stage_execute', {
        run_id: run.run_id,
        stage_id: 'refresh_3_competitor_extract',
        extraction_id: entryUrl,
      });
      await jobsService.waitForCompletion(job_id);
      await refreshRun();
      await refreshDocument();
      await refreshArticles();
    } catch (e: any) {
      console.error('Extraction failed:', e);
    } finally {
      setRunningSingle(false);
    }
  }, [run, refreshRun, refreshDocument, refreshArticles]);

  const runAll = useCallback(async () => {
    if (!run?.run_id) return;
    cancelRef.current = false;
    setRunningAll(true);
    setBatchStatus({});
    await blogRunsService.resetFromStage(run.run_id, 'refresh_3_competitor_extract');
    await refreshRun();
    for (const entry of urls) {
      const url = (entry as any).url;
      if (!url || cancelRef.current) break;
      setBatchStatus((prev) => ({ ...prev, [url]: { status: 'running' } }));
      try {
        const { job_id } = await jobsService.submit('blog_stage_execute', {
          run_id: run.run_id,
          stage_id: 'refresh_3_competitor_extract',
          extraction_id: url,
        });
        await jobsService.waitForCompletion(job_id);
        setBatchStatus((prev) => ({ ...prev, [url]: { status: 'succeeded' } }));
      } catch (e: any) {
        setBatchStatus((prev) => ({ ...prev, [url]: { status: 'failed', error: e?.message } }));
      }
    }
    setRunningAll(false);
    await refreshRun();
    await refreshDocument();
    await refreshArticles();
  }, [run, urls, refreshRun, refreshDocument, refreshArticles]);

  const selectedExtraction = extractions.find((e: any) => e.url === selectedUrl);

  return (
    <div className="space-y-3">
      {/* Batch controls */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
          disabled={runningAll || urls.length === 0}
          onClick={runAll}
        >
          {runningAll ? 'Running All...' : `Extract All (${urls.length})`}
        </button>
        {runningAll && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={() => { cancelRef.current = true; }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Competitor cards */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {urls.map((entry: any) => {
          const url = entry.url;
          const isExtracted = extractedUrls.has(url);
          const batch = batchStatus[url];
          const isAbove = entry.above_or_below_us === 'above';
          return (
            <button
              key={url}
              type="button"
              className={`flex-shrink-0 border rounded px-3 py-2 text-left text-xs w-44 ${
                selectedUrl === url ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              } ${isExtracted ? 'bg-green-50' : 'bg-white'}`}
              onClick={() => handleSelect(url)}
            >
              <div className="font-medium text-gray-800 truncate">{entry.domain}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-gray-400">#{entry.serp_position}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  isAbove ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                }`}>
                  {isAbove ? 'above us' : 'below us'}
                </span>
              </div>
              {batch && (
                <div className={`text-[10px] mt-1 ${
                  batch.status === 'succeeded' ? 'text-green-600' :
                  batch.status === 'failed' ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {batch.status}
                </div>
              )}
              {!batch && isExtracted && (
                <div className="text-[10px] mt-1 text-green-600">extracted</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected extraction detail */}
      {selectedUrl && (
        <div className="border rounded p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-gray-800 truncate flex-1">{selectedUrl}</div>
            <button
              type="button"
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 ml-2 shrink-0"
              disabled={runningSingle || runningAll}
              onClick={() => runSingle(selectedUrl)}
            >
              {runningSingle ? 'Running...' : 'Extract'}
            </button>
          </div>
          {selectedExtraction ? (
            <div className="text-xs text-gray-600 space-y-1.5">
              {selectedExtraction.page_title && (
                <div><span className="text-gray-400">Title:</span> {selectedExtraction.page_title}</div>
              )}
              {selectedExtraction.approximate_word_count && (
                <div><span className="text-gray-400">Words:</span> ~{selectedExtraction.approximate_word_count}</div>
              )}
              <div className="flex gap-3">
                {selectedExtraction.has_examples && <span className="text-green-600">Has examples</span>}
                {selectedExtraction.has_tables && <span className="text-green-600">Has tables</span>}
                {selectedExtraction.has_images && <span className="text-green-600">Has images</span>}
              </div>
              {selectedExtraction.headings?.length > 0 && (
                <div>
                  <span className="text-gray-400">Headings:</span>
                  <ul className="ml-3 mt-0.5 list-disc text-gray-500">
                    {selectedExtraction.headings.map((h: string, i: number) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedExtraction.topics_covered?.length > 0 && (
                <div>
                  <span className="text-gray-400">Topics:</span>{' '}
                  <span className="text-gray-500">{selectedExtraction.topics_covered.join(', ')}</span>
                </div>
              )}
              {selectedExtraction.stats_cited?.length > 0 && (
                <div>
                  <span className="text-gray-400">Stats cited:</span>
                  <ul className="ml-3 mt-0.5 list-disc text-gray-500">
                    {selectedExtraction.stats_cited.map((s: any, i: number) => (
                      <li key={i}>{s.stat} <span className="text-gray-400">({s.source})</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedExtraction.notable_features && (
                <div><span className="text-gray-400">Notable:</span> {selectedExtraction.notable_features}</div>
              )}
              {selectedExtraction.status === 'failed' && (
                <div className="text-red-500">Error: {selectedExtraction.error || 'Unknown error'}</div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-400">Not yet extracted. Click Extract to fetch and analyze.</div>
          )}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const extractions = (doc.targeting?.competitors?.extractions || []) as any[];
  if (extractions.length === 0) {
    return <div className="text-sm text-gray-500">No extractions yet.</div>;
  }
  const succeeded = extractions.filter((e: any) => e.status !== 'failed');
  return (
    <div>
      <div className="text-xs font-medium text-gray-700 mb-1">
        Extracted {succeeded.length} of {extractions.length} competitors
      </div>
      <div className="space-y-1">
        {extractions.map((e: any, i: number) => (
          <div key={i} className="text-xs border rounded px-2 py-1.5 border-gray-200 flex items-center justify-between">
            <div>
              <span className="font-medium">{e.domain}</span>
              <span className="text-gray-400 ml-1">#{e.serp_position}</span>
              <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                e.above_or_below_us === 'above' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
              }`}>
                {e.above_or_below_us}
              </span>
              {e.page_title && <span className="text-gray-400 ml-2">— {e.page_title}</span>}
            </div>
            <span className="text-gray-400">~{e.approximate_word_count || '?'} words</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RefreshCompetitorExtractV2() {
  const { document } = useArticleContext();
  const hasUrls = ((document?.targeting?.competitors?.urls || []) as any[]).length > 0;
  const [selectedExtractionId, setSelectedExtractionId] = useState<string | undefined>(undefined);

  return (
    <StagePanel
      stageId="refresh_3_competitor_extract"
      title="Refresh: Competitor Extraction"
      description="Fetches and analyzes content from each competitor URL identified by the SERP stage."
      hideRunButton
      isLlmStage
      extractionId={selectedExtractionId}
      renderOutput={renderOutput}
      runDisabled={!hasUrls}
    >
      {hasUrls ? <CompetitorCards onSelectUrl={setSelectedExtractionId} /> : (
        <div className="text-xs text-gray-500">No competitor URLs. Run the SERP stage first.</div>
      )}
    </StagePanel>
  );
}
